import React, { useEffect } from 'react'
import { AgreementPageProps } from './AgreementPage.types'
import styles from './AgreementPage.module.scss'
import cl from 'classnames'
import {
  ButtonProps,
  CurrencySelector,
  H2,
  Input,
  PageHeader,
  SectionContainer,
  TablePeriodFilter,
  Text1,
  VerticalLine
} from 'components/shared'
import { IconAgreements } from 'assets'
import { ScopeSection } from './ScopeSection'
import { inject } from 'mobx-react'
import { AGREEMENT_PAGE_STORE, ROUTING_STORE } from 'store'
import { observer } from 'mobx-react-lite'
import {
  AgreementItemPropertyTypes,
  AgreementItemTypes,
  AgreementStatusTypes,
  getAgreementStatusLabel
} from 'models'
import { capitalize } from '@material-ui/core'
import { useGetIsResponsiveScreen, User } from '../../../utilities'
import { AgreementPageHeaderSecondLine } from '../AgreementPageHeaderSecondLine'

const AgreementPageComponent = (props: AgreementPageProps) => {
  const {
    [AGREEMENT_PAGE_STORE]: {
      navLabel,
      agreement,
      setAgreementProps,
      getItemProp,
      setAgreementItemProp,
      period,
      onPeriodChange,
      isAgreementReadyToSave,
      clearStore,
      isEditing,
      updateAgreementStatus,
      agreementStatus,
      getChangeByKeyId
    },
    [ROUTING_STORE]: {
      push,
      goBack
    },
    onSubmit
  } = props

  const { isResponsive: isLargeMobile } = useGetIsResponsiveScreen('(max-width: 500px)')

  useEffect(() => clearStore, [clearStore])

  if (!agreement) return null

  const onSave = (agreementStatus: AgreementStatusTypes) => {
    setAgreementProps({ agreementStatus })
    onSubmit()
  }

  const getSaveButtons = (): ButtonProps[] => {
    const buttons: ButtonProps[] = []
    if (User.isAdmin()) {
      buttons.push(
        {
          children: 'Discard',
          preset: 'secondary',
          onClick: goBack
        },
        {
          children: 'Save',
          preset: 'primary',
          onClick: onSubmit,
          disabled: !isAgreementReadyToSave
        }
      )
    } else if (agreement?.agreementStatus === AgreementStatusTypes.AGREED && !isAgreementReadyToSave) {
      buttons.push(
        {
          children: 'Discard',
          preset: 'secondary',
          onClick: goBack
        },
        {
          children: 'Save as draft', // todo if shop owner and budget increased - show update
          preset: 'primary',
          onClick: () => {
            onSave(User.isShopOwner()
              ? AgreementStatusTypes.DRAFT_STORE
              : AgreementStatusTypes.DRAFT_PROJECT)
          },
          disabled: !isAgreementReadyToSave
        },
        {
          children: 'Send for approval',
          preset: 'primary',
          onClick: () => {
            onSave(User.isShopOwner()
              ? AgreementStatusTypes.PENDING_PROJECT_APPROVAL
              : AgreementStatusTypes.PENDING_STORE_APPROVAL)
          },
          disabled: !isAgreementReadyToSave
        }
      )
    } else if (User.isShopOwner()) {
      if (!isAgreementReadyToSave && agreement?.agreementStatus === AgreementStatusTypes.PENDING_STORE_APPROVAL) {
        buttons.push({
          children: 'Confirm agreement',
          preset: 'primary',
          onClick: () => {
            agreement.agreementStatus = AgreementStatusTypes.AGREED
            updateAgreementStatus()
          },
          disabled: isAgreementReadyToSave
        })
      } else {
        buttons.push(
          {
            children: 'Save as draft',
            preset: 'primary',
            onClick: () => {
              onSave(AgreementStatusTypes.DRAFT_STORE)
            },
            disabled: !isAgreementReadyToSave
          },
          {
            children: 'Send for approval',
            preset: 'primary',
            onClick: () => {
              onSave(AgreementStatusTypes.PENDING_PROJECT_APPROVAL)
            },
            disabled: !isAgreementReadyToSave
          }
        )
      }
    } else if (User.isManager()) {
      if (!isAgreementReadyToSave && agreement?.agreementStatus === AgreementStatusTypes.PENDING_PROJECT_APPROVAL) {
        buttons.push({
          children: 'Confirm agreement',
          preset: 'primary',
          onClick: () => {
            agreement.agreementStatus = AgreementStatusTypes.AGREED
            updateAgreementStatus()
          },
          disabled: isAgreementReadyToSave
        })
      } else {
        buttons.push(
          {
            children: 'Discard',
            preset: 'secondary',
            onClick: goBack
          },
          {
            children: 'Save as draft',
            preset: 'primary',
            onClick: () => {
              onSave(AgreementStatusTypes.DRAFT_PROJECT)
            },
            disabled: !isAgreementReadyToSave
          },
          {
            children: 'Send for approval',
            preset: 'primary',
            onClick: () => {
              onSave(AgreementStatusTypes.PENDING_STORE_APPROVAL)
            },
            disabled: !isAgreementReadyToSave
          }
        )
      }
    }
    return buttons
  }

  return (
    <div className={cl(styles.pageContainer, styles.scrollable)}>
      <PageHeader
        navSegments={[
          {
            label: 'Agreements',
            icon: IconAgreements,
            onClick: () => push('/agreements')
          },
          {
            label: navLabel
          }
        ]}
        middleContainerChildren={(
          <>
            <CurrencySelector
              style={{ marginLeft: 'auto' }}
            />
            <VerticalLine
              style={{ margin: '0 5px 0 35px' }}
            />
          </>
        )}
        buttons={getSaveButtons()}
        secondLineContainerProps={isEditing
          ? {
            children: <AgreementPageHeaderSecondLine />
          }
          : undefined}
      />

      <SectionContainer className={styles.pageSection} extraPaddingBottom>
        <H2>Agreement details</H2>

        <div className={styles.agreementStatusContainer}>
          <div>
            <Text1 extraBold>Status:</Text1>
            <Text1
              style={{
                color: isEditing
                  ? styles.primary
                  : undefined
              }}
            >
              {capitalize(getAgreementStatusLabel(agreementStatus))}
            </Text1>
          </div>
          <div>
            <Text1 extraBold>Currently affecting a published project:</Text1>
            <Text1
              style={{
                color: isEditing
                  ? styles.primary
                  : undefined
              }}
            >No</Text1>
          </div>
        </div>

        <SectionContainer preset='secondary' className={styles.pageSection}>
          <Input
            label='Name'
            value={agreement.name}
            onChange={name => setAgreementProps({ name })}
            versionChange={getChangeByKeyId('name')}
          />

          <div className={cl(styles.flexContainer, styles.marginTop)}>
            <Input
              label='Budget'
              type='number'
              unitsText='USD'
              min={0}
              value={agreement.budget}
              onChangeNumber={budget => setAgreementProps({ budget })}
              versionChange={getChangeByKeyId('budget')}
            />
            <Input
              label='Buffer'
              type='number'
              unitsText='%'
              min={0}
              value={agreement.budgetBuffer}
              onChangeNumber={budgetBuffer => setAgreementProps({ budgetBuffer })}
              versionChange={getChangeByKeyId('budgetBuffer')}
            />
            <div className={styles.periodPickerContainer}>
              <Text1 extraBold style={{ marginBottom: 6 }}>Period</Text1>
              <TablePeriodFilter
                placeholder='Pick start and end date'
                period={period}
                setPeriod={onPeriodChange}
                className={styles.periodPicker}
                versionChange={getChangeByKeyId('period')}
              // noIcon={false}
              />
            </div>
          </div>
        </SectionContainer>
      </SectionContainer>

      <SectionContainer className={styles.pageSection} extraPaddingBottom>
        <H2>Configuration</H2>
        <SectionContainer preset='secondary' className={styles.pageSection}>
          <Text1 extraBold>BLOB</Text1>
          <div className={cl(styles.flexContainer, styles.marginTop)}>
            <div className={styles.flexContainer}>
              <Input
                label='Price per one trigger'
                unitsText='USD'
                type='number'
                min={0}
                value={getItemProp(
                  AgreementItemTypes.BLOB_SETTINGS,
                  AgreementItemPropertyTypes.BLOB_TRIGGERED
                )}
                onChangeNumber={setAgreementItemProp(
                  AgreementItemTypes.BLOB_SETTINGS,
                  AgreementItemPropertyTypes.BLOB_TRIGGERED
                )}
                versionChange={getChangeByKeyId(AgreementItemPropertyTypes.BLOB_TRIGGERED)}
              />
              <Input
                label='Max attach time'
                unitsText='seconds'
                type='number'
                min={0}
                value={getItemProp(
                  AgreementItemTypes.BLOB_SETTINGS,
                  AgreementItemPropertyTypes.MAX_ATTACH_TIME_SECONDS
                )}
                onChangeNumber={setAgreementItemProp(
                  AgreementItemTypes.BLOB_SETTINGS,
                  AgreementItemPropertyTypes.MAX_ATTACH_TIME_SECONDS
                )}
                versionChange={getChangeByKeyId(AgreementItemPropertyTypes.MAX_ATTACH_TIME_SECONDS)}
              />
            </div>
            <div>
              <Text1 extraBold style={{ marginBottom: 6 }}> {/* Remove horizontalFillLine */}
                Price per time on screen
              </Text1>
              <div className={styles.flexContainer}>
                <Input
                  label='Attached'
                  // labelPosition='left'
                  {...(!isLargeMobile && {
                    labelPosition: 'left'
                  })}
                  labelProps={{ bold: false, style: { color: styles.secondaryContent, width: 75, paddingRight: 35 } }}
                  unitsText='USD'
                  type='number'
                  min={0}
                  value={getItemProp(
                    AgreementItemTypes.BLOB_SETTINGS,
                    AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN_ATTACHED
                  )}
                  onChangeNumber={setAgreementItemProp(
                    AgreementItemTypes.BLOB_SETTINGS,
                    AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN_ATTACHED
                  )}
                  versionChange={getChangeByKeyId(AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN_ATTACHED)}
                />
                <Input
                  label='Detached'
                  // labelPosition='left'
                  {...(!isLargeMobile && {
                    labelPosition: 'left'
                  })}
                  labelProps={{ bold: false, style: { color: styles.secondaryContent, width: 75, paddingRight: 35 } }}
                  unitsText='USD'
                  type='number'
                  min={0}
                  value={getItemProp(
                    AgreementItemTypes.BLOB_SETTINGS,
                    AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN_DETACHED
                  )}
                  onChangeNumber={setAgreementItemProp(
                    AgreementItemTypes.BLOB_SETTINGS,
                    AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN_DETACHED
                  )}
                  versionChange={getChangeByKeyId(AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN_DETACHED)}
                />
              </div>
            </div>
            <div>
              <Text1 extraBold style={{ marginBottom: 6 }} > {/* Remove horizontalFillLine */}
                Time in stack
              </Text1>
              <div className={styles.flexContainer}>
                <Input
                  label='Min'
                  // labelPosition='left'
                  {...(!isLargeMobile && {
                    labelPosition: 'left'
                  })}
                  labelProps={{ bold: false, style: { color: styles.secondaryContent, width: 75, paddingRight: 35 } }}
                  unitsText='seconds'
                  type='number'
                  min={0}
                  value={getItemProp(
                    AgreementItemTypes.BLOB_SETTINGS,
                    AgreementItemPropertyTypes.MINIMUM_TIME_IN_STACK
                  )}
                  onChangeNumber={setAgreementItemProp(
                    AgreementItemTypes.BLOB_SETTINGS,
                    AgreementItemPropertyTypes.MINIMUM_TIME_IN_STACK
                  )}
                  versionChange={getChangeByKeyId(AgreementItemPropertyTypes.MINIMUM_TIME_IN_STACK)}
                />
                <Input
                  label='Max'
                  // labelPosition='left'
                  {...(!isLargeMobile && {
                    labelPosition: 'left'
                  })}
                  labelProps={{ bold: false, style: { color: styles.secondaryContent, width: 75, paddingRight: 35 } }}
                  unitsText='seconds'
                  type='number'
                  min={0}
                  value={getItemProp(
                    AgreementItemTypes.BLOB_SETTINGS,
                    AgreementItemPropertyTypes.MAXIMUM_TIME_IN_STACK
                  )}
                  onChangeNumber={setAgreementItemProp(
                    AgreementItemTypes.BLOB_SETTINGS,
                    AgreementItemPropertyTypes.MAXIMUM_TIME_IN_STACK
                  )}
                  versionChange={getChangeByKeyId(AgreementItemPropertyTypes.MAXIMUM_TIME_IN_STACK)}
                />
              </div>
            </div>
          </div>
        </SectionContainer>

        <div className={cl(styles.pageSection, styles.flexContainer)}>
          <SectionContainer preset='secondary' style={{ flex: 2 }}>
            <Text1 extraBold>PRODUCT</Text1>
            <div className={cl(styles.flexContainer, styles.marginTop)}>
              <Input
                label='Price per opened product page'
                // labelPosition='left'
                {...(!isLargeMobile && {
                  labelPosition: 'left'
                })}
                labelProps={{ style: { lineHeight: '17px', width: 150, paddingRight: 70 } }}
                unitsText='USD'
                type='number'
                min={0}
                value={getItemProp(
                  AgreementItemTypes.PRODUCT,
                  AgreementItemPropertyTypes.PRODUCT_PAGE_OPENED_FROM_BLOB
                )}
                onChangeNumber={setAgreementItemProp(
                  AgreementItemTypes.PRODUCT,
                  AgreementItemPropertyTypes.PRODUCT_PAGE_OPENED_FROM_BLOB
                )}
                versionChange={getChangeByKeyId(AgreementItemPropertyTypes.PRODUCT_PAGE_OPENED_FROM_BLOB)}
              />
              <Input
                label='Price per product added to cart'
                // labelPosition='left'
                {...(!isLargeMobile && {
                  labelPosition: 'left'
                })}
                labelProps={{ style: { lineHeight: '17px', width: 150, paddingRight: 70 } }}
                unitsText='USD'
                type='number'
                min={0}
                value={getItemProp(
                  AgreementItemTypes.PRODUCT,
                  AgreementItemPropertyTypes.PRODUCT_ADDED_TO_CART
                )}
                onChangeNumber={setAgreementItemProp(
                  AgreementItemTypes.PRODUCT,
                  AgreementItemPropertyTypes.PRODUCT_ADDED_TO_CART
                )}
                versionChange={getChangeByKeyId(AgreementItemPropertyTypes.PRODUCT_ADDED_TO_CART)}
              />
            </div>

          </SectionContainer>
          <SectionContainer preset='secondary'>
            <Text1 extraBold>VIDEO COMMERCIAL</Text1>
            <Input
              label='Price per video playback'
              // labelPosition='left'
              {...(!isLargeMobile && {
                labelPosition: 'left'
              })}
              labelProps={{ style: { lineHeight: '17px', width: 150, paddingRight: 70 } }}
              containerProps={{ className: styles.marginTop }}
              unitsText='USD'
              type='number'
              min={0}
              value={getItemProp(
                AgreementItemTypes.VIDEO_COMMERCIAL,
                AgreementItemPropertyTypes.PRICE_PER_VIDEO_PLAYBACK
              )}
              onChangeNumber={setAgreementItemProp(
                AgreementItemTypes.VIDEO_COMMERCIAL,
                AgreementItemPropertyTypes.PRICE_PER_VIDEO_PLAYBACK
              )}
              versionChange={getChangeByKeyId(AgreementItemPropertyTypes.PRICE_PER_VIDEO_PLAYBACK)}
            />
          </SectionContainer>
        </div>

        <div className={cl(styles.pageSection, styles.flexContainer)}>
          <SectionContainer preset='secondary' style={{ flex: 2 }}>
            <Text1 extraBold>POSTER</Text1>
            <div className={cl(styles.flexContainer, styles.marginTop)}>
              <Input
                label='Price per click'
                // labelPosition='left'
                {...(!isLargeMobile && {
                  labelPosition: 'left'
                })}
                labelProps={{ style: { lineHeight: '17px', width: 150, paddingRight: 70 } }}
                unitsText='USD'
                type='number'
                min={0}
                value={getItemProp(
                  AgreementItemTypes.POSTER,
                  AgreementItemPropertyTypes.PRICE_PER_CLICK
                )}
                onChangeNumber={setAgreementItemProp(
                  AgreementItemTypes.POSTER,
                  AgreementItemPropertyTypes.PRICE_PER_CLICK
                )}
                versionChange={getChangeByKeyId(AgreementItemPropertyTypes.PRICE_PER_CLICK)}
              />
              <Input
                label='Price per second on screen'
                // labelPosition='left'
                {...(!isLargeMobile && {
                  labelPosition: 'left'
                })}
                labelProps={{ style: { lineHeight: '17px', width: 150, paddingRight: 70 } }}
                unitsText='USD'
                type='number'
                min={0}
                value={getItemProp(
                  AgreementItemTypes.POSTER,
                  AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN
                )}
                onChangeNumber={setAgreementItemProp(
                  AgreementItemTypes.POSTER,
                  AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN
                )}
                versionChange={getChangeByKeyId(AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN)}
              />
            </div>

          </SectionContainer>
          <SectionContainer preset='secondary'>
            <Text1 extraBold>PRODUCT PURCHASE</Text1>
            <Input
              label='Purchase fee'
              // labelPosition='left'
              {...(!isLargeMobile && {
                labelPosition: 'left'
              })}
              labelProps={{ style: { lineHeight: '17px', width: 150, paddingRight: 70 } }}
              containerProps={{ className: styles.marginTop }}
              unitsText='%'
              type='number'
              min={0}
              value={getItemProp(
                AgreementItemTypes.PRODUCT_PURCHASE,
                AgreementItemPropertyTypes.PURCHASE_FEE
              )}
              onChangeNumber={setAgreementItemProp(
                AgreementItemTypes.PRODUCT_PURCHASE,
                AgreementItemPropertyTypes.PURCHASE_FEE
              )}
              versionChange={getChangeByKeyId(AgreementItemPropertyTypes.PURCHASE_FEE)}
            />
          </SectionContainer>
        </div>
      </SectionContainer>

      <ScopeSection />
    </div >
  )
}

const AgreementPage = inject(
  AGREEMENT_PAGE_STORE,
  ROUTING_STORE
)(observer(AgreementPageComponent))
export { AgreementPage }
